import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  EWalletShoppingCartItemPayload,
  FeeShoppingCartItemPayload,
  OffenseShoppingCartItemPayload,
  ShoppingCartItemVm,
  ShoppingCartVm,
  TicketFareShoppingCartItemPayload,
} from '@/api';
import shoppingCartService from '@/services/api/shoppingCartService';
import isDefined, { isArrayEmpty } from '@common/utils/isDefined';
import { useUserAccountStore } from '@/store/useUserAccountStore';
import { useToastStore } from '@/store/useToastStore';
import { getEarliestTicketFareValidFrom } from '@/utils/ticketFare';
import { isBefore, startOfDay } from 'date-fns';
import { convertTimeToString, getRemainingTime } from '@/utils/dateTime';
import accountService from '@/services/api/accountService';

export const MIN_DAYS_BETWEEN_TICKET_FARE_VALID_FROM_AND_NOW = 3;

export const useShoppingCartStore = defineStore('shoppingCart', () => {
  const items = ref<ShoppingCartItemVm[]>([]);
  const ticketFareParentIds = ref<string[]>([]);
  const totalPrice = ref(0);
  const itemsCount = ref(0);
  const remainingTime = ref<string | undefined>('00:00:00');
  const expirationTime = ref<string | undefined>(undefined);
  const lifeTimeTimer = ref<NodeJS.Timeout | number | null>(null);

  const isEmpty = computed(() => {
    return isArrayEmpty(items.value);
  });

  const isCardApplicationInShoppingCartForSelectedUser = computed(() => {
    const userAccountStore = useUserAccountStore();
    return items.value.some(item => {
      return (
        (item.payload as FeeShoppingCartItemPayload)?.applicationId &&
        (item.payload as FeeShoppingCartItemPayload)?.originId === userAccountStore.selectedCustomerData.id
      );
    });
  });

  const isCardApplicationInShoppingCart = computed(() => {
    return items.value.some(item => (item.payload as FeeShoppingCartItemPayload)?.applicationId);
  });

  const isReChargeWalletRequestInShoppingCart = computed(() => {
    const userAccountStore = useUserAccountStore();
    return items.value.some(
      item =>
        (item.payload as EWalletShoppingCartItemPayload)?.eWalletId &&
        (item.payload as EWalletShoppingCartItemPayload)?.cardNumber === userAccountStore.cardNumber,
    );
  });

  const isTicketFareInShoppingCart = computed(() => {
    return !!findTicketFareItem.value;
  });

  const findTicketFareItem = computed(() => {
    const userAccountStore = useUserAccountStore();
    return items.value.find(
      item =>
        (item.payload as TicketFareShoppingCartItemPayload)?.ticketFareId &&
        (item.payload as TicketFareShoppingCartItemPayload)?.cardId === userAccountStore.cardDetailData.id,
    );
  });

  const isTicketFareTooSoon = computed(() => {
    const earliestValidFrom = getEarliestTicketFareValidFrom();
    return items.value.some(item => {
      const ticketFareValidFrom = (item.payload as TicketFareShoppingCartItemPayload)?.ticketFareValidFrom;
      if (!(item.payload as TicketFareShoppingCartItemPayload)?.ticketFareId || !ticketFareValidFrom) {
        return false;
      }
      const validFrom = startOfDay(ticketFareValidFrom);
      return isBefore(validFrom, earliestValidFrom);
    });
  });

  const getExpirationTime = computed(() => {
    return expirationTime.value;
  });

  const offenseItems = computed(() => {
    return items.value.filter(item => (item.payload as OffenseShoppingCartItemPayload)?.offenseId);
  });

  const setShoppingCart = (payload: ShoppingCartVm) => {
    items.value = payload.items;
    totalPrice.value = payload.totalPrice;
    ticketFareParentIds.value = [];
    for (const item of payload.items) {
      if (isDefined((item.payload as TicketFareShoppingCartItemPayload)?.ticketFareParentId)) {
        ticketFareParentIds.value.push(
          (item.payload as TicketFareShoppingCartItemPayload).ticketFareParentId as string,
        );
      }
    }
  };

  const setExpirationTime = (value?: string) => {
    expirationTime.value = value;
  };

  const clearLifetimeTimer = () => {
    if (lifeTimeTimer.value) {
      clearInterval(lifeTimeTimer.value);
    }
  };

  const updateRemainingTime = (value?: string) => {
    remainingTime.value = value;
  };

  const setItemsCount = (value?: number) => {
    if (isDefined(value)) {
      itemsCount.value = value!;
    }
  };

  const setLifetimeTimer = (timer: NodeJS.Timeout | number | null) => {
    lifeTimeTimer.value = timer;
  };

  const updateShoppingCart = async () => {
    const shoppingCart = await shoppingCartService.getShoppingCart();
    if (shoppingCart) {
      setShoppingCart(shoppingCart);
    }
  };

  const updateItemsCount = async () => {
    const shoppingCartCount = await shoppingCartService.getShoppingCartCount();
    setItemsCount(shoppingCartCount);
  };

  const setCartLifetimeTimer = async () => {
    const toastStore = useToastStore();

    if (getExpirationTime.value) {
      const expirationTime = new Date(getExpirationTime.value).getTime();
      const dateNowForCheck = Date.now();
      const remainingTimeForCheck = expirationTime - dateNowForCheck;

      if (remainingTimeForCheck <= 0) {
        await nullLifetimeAndClearCart();
        toastStore.error({ message: 'Vypršel časový limit košíku. Všechny položky byly odebrány.' });
        return;
      }

      if (lifeTimeTimer.value) {
        clearLifetimeTimer();
      }

      // Show the value right away, the interval ticks for the first time after a second.
      updateRemainingTime(convertTimeToString(remainingTimeForCheck));

      const timer = setInterval(() => {
        const remainingTime = getRemainingTime(expirationTime);

        if (remainingTime <= 0) {
          nullLifetimeAndClearCart();
          toastStore.error({ message: 'Vypršel časový limit košíku. Všechny položky byly odebrány.' });
        } else {
          updateRemainingTime(convertTimeToString(remainingTime));
        }
      }, 1000);

      setLifetimeTimer(timer);
    }
  };

  const nullLifetimeAndClearCart = async () => {
    await nullCartLifetime();
    await shoppingCartService.removeAllItems();
    await updateShoppingCart();
    await updateItemsCount();
  };

  const nullCartLifetime = async () => {
    clearLifetimeTimer();
    updateRemainingTime('00:00:00');
    await accountService.nullCustomerAccountCartLifeTime();
  };

  const updateLifetimeTimer = () => {
    clearLifetimeTimer();
    setCartLifetimeTimer();
  };

  const getAndUpdateCartExpirationTime = async () => {
    const response = await accountService.getCustomerAccountCartLifetime();

    setExpirationTime(response.data.cartLifetime);
  };

  /**
   * Loads the cart expiration from the server into the store.
   *
   * @returns whether the cart lifetime already passed (a cart without lifetime counts as expired)
   */
  const refreshCartExpirationTime = async (): Promise<boolean> => {
    await getAndUpdateCartExpirationTime();
    if (!expirationTime.value) {
      return true;
    }
    return new Date(expirationTime.value).getTime() - Date.now() <= 0;
  };

  /**
   * Reloads the whole cart from the server: clears it when its lifetime passed or is missing, otherwise
   * (re)starts the countdown from the server lifetime. The store does not know the expiration
   * when the cart was filled by a flow that skipped starting the timer (e.g. a card application
   * saved with a redirect to the cart), so the countdown is synced on every reload.
   */
  const refreshShoppingCart = async () => {
    const isExpired = await refreshCartExpirationTime();
    if (isExpired) {
      await nullLifetimeAndClearCart();
      return;
    }
    await updateShoppingCart();
    if (isEmpty.value) {
      await nullCartLifetime();
    } else {
      await setCartLifetimeTimer();
    }
    await updateItemsCount();
  };

  return {
    items,
    ticketFareParentIds,
    totalPrice,
    remainingTime,
    expirationTime,
    lifeTimeTimer,

    isEmpty,
    isCardApplicationInShoppingCartForSelectedUser,
    isCardApplicationInShoppingCart,
    isReChargeWalletRequestInShoppingCart,
    isTicketFareInShoppingCart,
    isTicketFareTooSoon,
    offenseItems,
    findTicketFareItem,
    getExpirationTime,
    itemsCount,

    setItemsCount,
    setShoppingCart,
    setExpirationTime,
    setCartLifetimeTimer,
    updateShoppingCart,
    updateLifetimeTimer,
    refreshShoppingCart,
    getAndUpdateCartExpirationTime,
    nullLifetimeAndClearCart,
    nullCartLifetime,
    updateItemsCount,
  };
});
